import type { JSX, ReactNode } from 'react';
import { Fragment, createContext, useContext } from 'react';

import type * as models from 'pkg/api/models';
import { mentionLabel } from 'pkg/chat_markup';

import { addFormatter } from 'components/formatted-content/FormattedContent';

import * as css from './styles.css';

/**
 *	The members a mention can name. Formatters are a global registry, so the
 *	`mentions` decorator can't take them as an argument; the chat provides
 *	them around its thread instead. Without a provider a token stays as typed.
 */
const ChatUsersContext = createContext<models.chatUser.ChatUser[]>([]);

interface ChatUsersProviderProps {
	users: models.chatUser.ChatUser[];
	children: ReactNode;
}

export function ChatUsersProvider({
	users,
	children,
}: ChatUsersProviderProps): JSX.Element {
	return (
		<ChatUsersContext.Provider value={users}>
			{children}
		</ChatUsersContext.Provider>
	);
}

/** The members the chat around provides, for text drawn outside a formatter. */
export function useChatUsers(): models.chatUser.ChatUser[] {
	return useContext(ChatUsersContext);
}

interface MentionProps {
	userId: number;
	token: string;
}

/** `@First Last`, in bold in the surrounding text colour, as Figma draws it. */
function Mention({ userId, token }: MentionProps): JSX.Element {
	const users = useContext(ChatUsersContext);
	const label = mentionLabel(userId, users);

	if (!label) {
		return <Fragment>{token}</Fragment>;
	}

	return (
		<span className={css.mention} data-user-id={userId}>
			{label}
		</span>
	);
}

addFormatter('mentions', {
	selector: /(@\{\d+\})/g,
	decorator: (guid: string, match: string): JSX.Element => {
		const userId = Number.parseInt(match.slice(2, -1), 10);

		return <Mention key={guid} userId={userId} token={match} />;
	},
});
